import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { useResponsive } from '../hooks/useResponsive';

interface ProfileEditModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (userData: Partial<User>) => void;
  isLoading?: boolean;
}

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  isOpen,
  user,
  onClose,
  onSave,
  isLoading = false
}) => {
  const { isMobile } = useResponsive();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    nickname: user?.nickname || '',
    position: user?.position || 'Mediocampista',
    rating: user?.rating || 3,
    preferredFoot: user?.preferredFoot || 'Derecho',
    preferredPosition: user?.preferredPosition || 'Mediocampista',
    description: user?.description || '',
    heightCm: user?.heightCm ? String(user.heightCm) : '',
    paymentAlias: user?.paymentAlias ?? '',
    paymentCbu: user?.paymentCbu ?? ''
  });
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [heightError, setHeightError] = useState<string | null>(null);

  // Actualizar estado cuando cambie el usuario
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        nickname: user.nickname || '',
        position: user.position || 'Mediocampista',
        rating: user.rating || 3,
        preferredFoot: user.preferredFoot || 'Derecho',
        preferredPosition: user.preferredPosition || 'Mediocampista',
        description: user.description || '',
        heightCm: user.heightCm ? String(user.heightCm) : '',
        paymentAlias: user.paymentAlias ?? '',
        paymentCbu: user.paymentCbu ?? ''
      });
      setNicknameError(null);
    }
  }, [user]);

  const validateNickname = (value: string): string | null => {
    if (!value) return null;
    if (value.length < 2 || value.length > 20) {
      return 'El nickname debe tener entre 2 y 20 caracteres';
    }
    if (!/^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_-]+$/.test(value)) {
      return 'Solo letras, números, guiones y guiones bajos';
    }
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nickErr = validateNickname(formData.nickname.trim());
    if (nickErr) {
      setNicknameError(nickErr);
      return;
    }
    const parsedHeight = formData.heightCm.trim() ? Number(formData.heightCm) : null;
    if (parsedHeight !== null && (Number.isNaN(parsedHeight) || parsedHeight < 120 || parsedHeight > 230)) {
      setHeightError('La altura debe estar entre 120 y 230 cm');
      return;
    }
    setHeightError(null);
    onSave({
      ...formData,
      nickname: formData.nickname.trim() || undefined,
      heightCm: parsedHeight,
      paymentAlias: formData.paymentAlias.trim() || null,
      paymentCbu: formData.paymentCbu.trim() || null
    });
  };

  if (!isOpen || !user) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        maxWidth: isMobile ? 'calc(100vw - 20px)' : '500px',
        width: '100%',
        maxHeight: isMobile ? 'calc(100vh - 20px)' : '90vh',
        border: '1px solid #33383B',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <h2 style={{
          margin: '0 0 20px 0',
          color: '#FFFFFF',
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          Editar Perfil
        </h2>

        <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="profile-edit-name" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              Nombre:
            </label>
            <input
              id="profile-edit-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
              disabled={isLoading}
              required
              minLength={1}
              maxLength={100}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="profile-edit-nickname" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              Nickname:
            </label>
            <input
              id="profile-edit-nickname"
              type="text"
              value={formData.nickname}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({...formData, nickname: value});
                setNicknameError(validateNickname(value.trim()));
              }}
              placeholder="ej: pelusa10"
              autoCapitalize="none"
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: `1px solid ${nicknameError ? '#FF5252' : '#40444B'}`,
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
              disabled={isLoading}
              maxLength={20}
            />
            <div style={{
              fontSize: isMobile ? '11px' : '12px',
              color: nicknameError ? '#FF5252' : '#B0BEC5',
              marginTop: '4px'
            }}>
              {nicknameError ?? 'Apodo público (opcional, 2-20 caracteres)'}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="profile-edit-position" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              Posición Actual:
            </label>
            <select
              id="profile-edit-position"
              value={formData.position}
              onChange={(e) => setFormData({...formData, position: e.target.value})}
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
              disabled={isLoading}
            >
              <option value="Arquero">🥅 Arquero</option>
              <option value="Defensor">🛡️ Defensor</option>
              <option value="Mediocampista">⚽ Mediocampista</option>
              <option value="Delantero">🚀 Delantero</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="profile-edit-preferred-position" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              Posición Preferida:
            </label>
            <select
              id="profile-edit-preferred-position"
              value={formData.preferredPosition}
              onChange={(e) => setFormData({...formData, preferredPosition: e.target.value})}
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
              disabled={isLoading}
            >
              <option value="Arquero">🥅 ARQ</option>
              <option value="Defensor">🛡️ DEF</option>
              <option value="Mediocampista">⚽ MC</option>
              <option value="Delantero">🚀 DEL</option>
            </select>
            <div style={{
              fontSize: isMobile ? '11px' : '12px',
              color: '#B0BEC5',
              marginTop: '4px'
            }}>
              Tu posición preferida para jugar
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="profile-edit-foot" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              Pie Hábil:
            </label>
            <select
              id="profile-edit-foot"
              value={formData.preferredFoot}
              onChange={(e) => setFormData({...formData, preferredFoot: e.target.value as 'Derecho' | 'Izquierdo' | 'Ambos'})}
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
              disabled={isLoading}
            >
              <option value="Derecho">🦶 Derecho</option>
              <option value="Izquierdo">🦶 Izquierdo</option>
              <option value="Ambos">🦶 Ambos</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="profile-edit-height" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              📏 Altura (cm):
            </label>
            <input
              id="profile-edit-height"
              type="number"
              inputMode="numeric"
              value={formData.heightCm}
              onChange={(e) => {
                setFormData({ ...formData, heightCm: e.target.value.replace(/\D/g, '').slice(0, 3) });
                if (heightError) setHeightError(null);
              }}
              placeholder="ej: 178"
              min={120}
              max={230}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: `1px solid ${heightError ? '#FF5252' : '#40444B'}`,
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
              disabled={isLoading}
            />
            <div style={{
              fontSize: isMobile ? '11px' : '12px',
              color: heightError ? '#FF5252' : '#B0BEC5',
              marginTop: '4px'
            }}>
              {heightError ?? 'Opcional. Solo la ve el organizador, para balancear equipos por altura.'}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="profile-edit-description" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              📝 Descripción Personal:
            </label>
            <textarea
              id="profile-edit-description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Cuéntanos un poco sobre ti, tu estilo de juego, experiencia..."
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px',
                minHeight: '80px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              disabled={isLoading}
              maxLength={500}
            />
            <div style={{
              fontSize: isMobile ? '11px' : '12px',
              color: '#B0BEC5',
              marginTop: '4px',
              textAlign: 'right'
            }}>
              {formData.description.length}/500 caracteres
            </div>
          </div>

          <div style={{
            marginTop: '8px',
            paddingTop: '16px',
            borderTop: '1px solid #33383B',
            marginBottom: '12px'
          }}>
            <h3 style={{
              margin: '0 0 4px 0',
              color: '#FFFFFF',
              fontSize: isMobile ? '15px' : '17px',
              fontWeight: '700'
            }}>
              💸 Recibir pagos
            </h3>
            <p style={{
              margin: 0,
              color: '#B0BEC5',
              fontSize: isMobile ? '12px' : '13px'
            }}>
              Configurá tu alias o CBU para que los jugadores te transfieran
            </p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="profile-edit-payment-alias" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              Alias de cobro:
            </label>
            <input
              id="profile-edit-payment-alias"
              type="text"
              value={formData.paymentAlias}
              onChange={(e) => setFormData({...formData, paymentAlias: e.target.value.slice(0, 20)})}
              placeholder="ej: futbolero.juan"
              autoCapitalize="none"
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
              disabled={isLoading}
              maxLength={20}
            />
            <div style={{
              fontSize: isMobile ? '11px' : '12px',
              color: '#B0BEC5',
              marginTop: '4px'
            }}>
              Alias de MercadoPago, CBU virtual, etc.
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="profile-edit-payment-cbu" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              CBU:
            </label>
            <input
              id="profile-edit-payment-cbu"
              type="text"
              inputMode="numeric"
              value={formData.paymentCbu}
              onChange={(e) => setFormData({...formData, paymentCbu: e.target.value.replace(/\D/g, '').slice(0, 22)})}
              placeholder="22 dígitos"
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
              disabled={isLoading}
              maxLength={22}
            />
            <div style={{
              fontSize: isMobile ? '11px' : '12px',
              color: '#B0BEC5',
              marginTop: '4px'
            }}>
              CBU bancario de 22 dígitos
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <p id="profile-edit-rating-label" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: '16px',
              margin: '0 0 8px 0'
            }}>
              Nivel (1-5 estrellas):
            </p>
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              marginBottom: '8px'
            }} role="group" aria-labelledby="profile-edit-rating-label">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({...formData, rating: star})}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: isMobile ? '20px' : '24px',
                    cursor: 'pointer',
                    color: formData.rating >= star ? '#FFD600' : '#40444B',
                    transition: 'color 0.2s ease',
                    padding: '4px'
                  }}
                  disabled={isLoading}
                >
                  ★
                </button>
              ))}
            </div>
            <div style={{
              textAlign: 'center',
              color: '#B0BEC5',
              fontSize: isMobile ? '12px' : '14px'
            }}>
              {formData.rating} estrella{formData.rating !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Spacer para empujar los botones hacia abajo */}
          <div style={{ flex: 1 }} />

          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: 'auto'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#40444B',
                border: 'none',
                borderRadius: '8px',
                color: '#FFFFFF',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'background-color 0.2s ease',
                minHeight: isMobile ? '44px' : '48px'
              }}
              disabled={isLoading}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#4F545C';
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#40444B';
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#00E676',
                border: 'none',
                borderRadius: '8px',
                color: '#181A1B',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'background-color 0.2s ease',
                opacity: isLoading ? 0.6 : 1,
                minHeight: isMobile ? '44px' : '48px'
              }}
              disabled={isLoading}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#00C853';
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#00E676';
              }}
            >
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 